import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';

/**
 * Fetch the full `Space` record (including description, createdAt, status)
 * for the active space. The cross-space `useMeSpaces` only returns the flat
 * `SpaceSummary` (name / kind / role / status) — `useSpaceDetail` is for
 * surfaces that need to render or edit the full record (e.g. the General
 * tab on the space management page).
 *
 * Subscribes to `optr.space.archived` / `optr.space.unarchived` and any
 * member-role-changed events for the active space so the cached detail row
 * stays current after another tab mutates it.
 */
const KEY = (spaceId: string) => ['space', spaceId, 'detail'] as const;

export function useSpaceDetail(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  const { subscribe } = usePlatformEvents();

  const query = useQuery({
    queryKey: KEY(spaceId ?? ''),
    queryFn: () => spaceApi(spaceId!).space.get(),
    enabled: !!spaceId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!spaceId) return;
    const unsub = subscribe(
      [
        { eventType: 'optr.space.archived', spaceId },
        { eventType: 'optr.space.unarchived', spaceId },
        { eventType: 'optr.space.member.role-changed', spaceId },
        { eventType: 'optr.space.ownership-transferred', spaceId },
      ],
      () => {
        queryClient.invalidateQueries({ queryKey: KEY(spaceId) });
      },
    );
    return unsub;
  }, [spaceId, subscribe, queryClient]);

  return query;
}
